import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'

import type { ImportHistoryDownLoadType, ImportHistoryType } from 'api/import_history/types'

import { getImportHistory, selectImportHistoryStatus } from 'slices/importHistorySlice'

import useBusinessTime from 'hooks/useBusinessTime'
import { useImportHistoryQuery, DEFAULT_PAGE } from 'hooks/useImportHistoryQuery'
import { useQuery } from 'hooks/useQuery'

import FilteringButton from '../FilteringButton/FilteringButton'
import PaginationAtoms from '../Pagination/Pagination'
import { PeriodSelect } from '../PeriodSelect/PeriodSelect'

import { ImportHistoryTable } from './ImportHistoryTable'
import { FILTER_LABELS, LIMIT_PER_PAGE } from './constants'

export type Category = {
  key: number
  label: string
  value: ImportHistoryDownLoadType
}
type Props = {
  importHistoryType: ImportHistoryType
  categories: Category[]
}

export const CustomImportHistory = ({ importHistoryType, categories }: Props) => {
  const dispatch = useDispatch()
  const { importHistory } = useSelector(selectImportHistoryStatus, shallowEqual)
  const { getWorkDate } = useBusinessTime()
  const { queryStart, queryEnd, setDateRange, queryPage, setQueryPage } = useImportHistoryQuery()
  const [selectedCategoryQuery, setSelectedCategoryQuery] = useQuery(
    'selectedCategoryIds',
    categories.map(category => category.key).join(',')
  )

  useEffect(() => {
    dispatch(
      getImportHistory({
        workDateFrom: queryStart,
        workDateTo: queryEnd,
        page: queryPage,
        limit: LIMIT_PER_PAGE,
        type: importHistoryType,
      })
    )
  }, [dispatch, queryStart, queryEnd, queryPage, importHistoryType])

  const handlePeriodChange = useCallback(
    (start: Date, end: Date) => {
      setDateRange(dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD'))
    },
    [setDateRange]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryPage(page)
    },
    [setQueryPage]
  )

  const selectedCategoryIds = useMemo(
    () => (selectedCategoryQuery.length === 0 ? [] : selectedCategoryQuery.split(',').map(Number)),
    [selectedCategoryQuery]
  )
  const handleFilterChange = useCallback(
    (filterItems: number[]) => setSelectedCategoryQuery(filterItems.length > 0 ? filterItems.join(',') : ''),
    [setSelectedCategoryQuery]
  )

  const importHistoryFilterItems = useMemo(
    () =>
      categories.map(category => ({
        key: category.key,
        label: category.label,
        checked: selectedCategoryIds.includes(category.key),
      })),
    [categories, selectedCategoryIds]
  )

  const filteredImportHistories = useMemo(() => {
    const filterIds = importHistoryFilterItems.filter(category => category.checked).map(category => category.key)
    return importHistory?.fileInfoData.filter(file => filterIds.includes(file.categoryId)) || []
  }, [importHistory?.fileInfoData, importHistoryFilterItems])

  const filteringButtonLabel = useMemo(
    () =>
      importHistoryFilterItems.every(item => item.checked)
        ? FILTER_LABELS[importHistoryType].ALL_SELECTED
        : FILTER_LABELS[importHistoryType].SOME_SELECTED,
    [importHistoryFilterItems, importHistoryType]
  )

  const importHistoryPeriodList = useMemo(() => {
    const baseDate = dayjs(getWorkDate(dayjs().format('YYYY-MM-DD')))
    return [
      {
        label: '今日',
        periodStart: baseDate.toDate(),
        periodEnd: baseDate.toDate(),
        id: 'today',
      },
      {
        label: '昨日',
        periodStart: baseDate.subtract(1, 'day').toDate(),
        periodEnd: baseDate.subtract(1, 'day').toDate(),
        id: 'yesterday',
      },
      {
        label: '開始日と終了日を選択',
        periodStart: baseDate.toDate(),
        periodEnd: baseDate.toDate(),
        id: 'user_setting',
      },
    ]
  }, [getWorkDate])

  return (
    <div className="d-flex flex-column row-gap-3">
      <div className="d-flex justify-content-between">
        <FilteringButton
          items={importHistoryFilterItems}
          label={filteringButtonLabel}
          onChange={handleFilterChange}
          value={selectedCategoryIds}
        />
        <PeriodSelect
          periodListData={importHistoryPeriodList}
          period={{ start: dayjs(queryStart).toDate(), end: dayjs(queryEnd).toDate() }}
          maxDate={dayjs(getWorkDate(dayjs().format('YYYY-MM-DD'))).toDate()}
          onChange={handlePeriodChange}
        />
      </div>
      <ImportHistoryTable
        selectedFileInfoData={filteredImportHistories}
        importHistoryType={importHistoryType}
        categories={categories}
      />
      <div className="d-flex justify-content-center">
        <PaginationAtoms
          currentPage={queryPage}
          totalPages={importHistory?.maxPage || DEFAULT_PAGE}
          onClick={handlePageChange}
        />
      </div>
    </div>
  )
}
